import * as Reducers from "./reducers";
import * as Self from "./AppDataProvider";
import { Action, ActionType } from "./actions";
import { AppData, AppDataProviderProps, Step } from "./types";
import React, { createContext, useEffect, useMemo, useReducer } from "react";
import i18n, { isSupportedLanguageCode } from "../i18n";
export * from "./types";
export const navigationSteps: Step[] = ["organisations", "services", "availability", "recurringBooking", "details", "bookingCreated"];

/**
 * In theory organisations is the first possible step,
 * however the getFirstStep function decides on booking tool
 * load which step to use first.
 */
const initialState: Omit<AppData, "bookingTool"> = {
  bookingData: {},
  currentStep: "organisations",
  steps: navigationSteps,
  toast: []
};
type AppContext = {
  appData: AppData;
  appDataDispatch: React.Dispatch<Action>;
};
export const AppContext = createContext<AppContext>({
  appData: initialState as AppData,
  appDataDispatch: {} as React.Dispatch<Action>
});
export const appDataReducer = (appData: AppData, action: Action) => {
  switch (action.type) {
    case ActionType.RESTART_BOOKING_FLOW:
      return Reducers.restartBookingFlow({
        appData
      });
    case ActionType.COMPLETE_ORGANISATION_STEP:
      return Reducers.completeOrganisationStep({
        appData,
        payload: action.payload
      });
    case ActionType.BACK_FROM_SERVICES_STEP:
      return Reducers.backFromServicesStep({
        appData
      });
    case ActionType.COMPLETE_SERVICES_STEP:
      return Reducers.completeServicesStep({
        appData,
        payload: action.payload
      });
    case ActionType.COMPLETE_MULTI_TIER_STEP:
      return Reducers.completeMultiTierStep({
        appData,
        payload: action.payload
      });
    case ActionType.BACK_FROM_MULTI_TIER_STEP:
      return Reducers.backFromMultiTierStep({
        appData
      });
    case ActionType.BACK_FROM_RESOURCES_STEP:
      return Reducers.backFromResourcesStep({
        appData
      });
    case ActionType.COMPLETE_RESOURCES_STEP:
      return Reducers.completeResourcesStep({
        appData,
        payload: action.payload
      });
    case ActionType.COMPLETE_PRICE_POINTS_STEP:
      return Reducers.completePricePointsStep({
        appData,
        payload: action.payload
      });
    case ActionType.BACK_FROM_PRICE_POINTS_STEP:
      return Reducers.backFromPricePointsStep({
        appData
      });
    case ActionType.COMPLETE_AVAILABILITY_STEP:
      return Reducers.completeAvailabilityStep({
        appData,
        ...(action.payload && {
          payload: action.payload
        })
      });
    case ActionType.CHANGE_SELECTED_TIMEZONE:
      return Reducers.changeSelectedTimezone({
        appData,
        payload: action.payload
      });
    case ActionType.COMPLETE_PAYMENTS_STEP:
      return Reducers.completePaymentsStep({
        appData,
        payload: action.payload
      });
    case ActionType.APPLY_DISCOUNT_CODE:
      return Reducers.applyDiscountCode({
        appData,
        payload: action.payload
      });
    case ActionType.REMOVE_DISCOUNT_CODE:
      return Reducers.removeDiscountCode({
        appData
      });
    case ActionType.BACK_FROM_AVAILABILITY_STEP:
      return Reducers.backFromAvailabilityStep({
        appData
      });
    case ActionType.BACK_FROM_DETAILS_STEP:
      return Reducers.backFromDetailsStep({
        appData,
        payload: action.payload
      });
    case ActionType.COMPLETE_DETAILS_STEP:
      return Reducers.completeDetailsStep({
        appData,
        payload: action.payload
      });
    case ActionType.BACK_FROM_PAYMENTS_STEP:
      return Reducers.backFromPaymentsStep({
        appData
      });
    case ActionType.RESET_AFTER_RESERVATION_EXPIRY:
      return Reducers.resetAfterReservationExpiry({
        appData
      });
    case ActionType.RESERVED_SPACE_DELETED:
      return Reducers.reservedSpaceDeleted({
        appData
      });
    case ActionType.BACK_FROM_BOOKING_QUESTIONS_STEP:
      return Reducers.backFromBookingQuestionsStep({
        appData,
        payload: action.payload
      });
    case ActionType.COMPLETE_BOOKING_QUESTIONS_STEP:
      return Reducers.completeBookingQuestionsStep({
        appData,
        payload: action.payload
      });
    case ActionType.BACK_FROM_RECURRING_BOOKING_STEP:
      return Reducers.backFromRecurringBookingStep({
        appData
      });
    case ActionType.COMPLETE_RECURRING_BOOKING_STEP:
      return Reducers.completeRecurringBookingStep({
        appData,
        ...(action.payload && {
          payload: action.payload
        })
      });
    case ActionType.COMPLETE_LOCATION_AVAILABILITY_STEP:
      return Reducers.completeLocationAvailabilityStep({
        appData,
        payload: action.payload
      });
    case ActionType.ADD_TOAST:
      return Reducers.addToast({
        appData,
        payload: action.payload
      });
    case ActionType.REMOVE_TOAST:
      return Reducers.removeToast({
        appData,
        payload: action.payload
      });
    case ActionType.SHOW_NON_MODAL_DIALOG:
      return Reducers.showNonModalDialog({
        appData,
        payload: action.payload
      });
    case ActionType.HIDE_NON_MODAL_DIALOG:
      return Reducers.hideNonModalDialog({
        appData
      });
    case ActionType.SHOW_MODAL_DIALOG:
      return Reducers.showModalDialog({
        appData,
        payload: action.payload
      });
    case ActionType.HIDE_MODAL_DIALOG:
      return Reducers.hideModalDialog({
        appData
      });
    case ActionType.DIALECT_DETECTED:
      return Reducers.detectedDialect({
        appData
      });
    case ActionType.NAVIGATE_TO_WAITLIST:
      return Reducers.navigateToWaitlist({
        appData,
        payload: action.payload
      });
    case ActionType.COMPLETE_WAITLIST:
      return Reducers.completeWaitlist({
        appData
      });
    case ActionType.BACK_FROM_WAITLIST_STEP:
      return Reducers.backFromWaitlistStep({
        appData
      });
  }
};
export function AppDataProvider(props: AppDataProviderProps) {
  const {
    children,
    bookingTool,
    bookingData,
    currentStep,
    modalDialog,
    nonModalDialog,
    reservationOnly,
    showReservationExpiry,
    userLocation,
    iFrameId,
    disableDialectDetection
  } = props;

  /**
   * Calling through the `Self` self-import (rather than the local
   * `appDataReducer` binding directly) is deliberate: it's the standard
   * workaround for `vi.spyOn(AppDataProvider, "appDataReducer")` in test
   * files to be able to intercept this call. Vite/Vitest's real-ESM module
   * transform (unlike Jest's CJS transpilation) does not rewrite a
   * module's own internal references to its exports to go through a
   * mutable exports object, so spying on the plain local binding here
   * would silently not work under Vitest even though the equivalent
   * pattern worked under Jest.
   */
  const [appData, appDataDispatch] = useReducer(Self.appDataReducer, {
    ...initialState,
    bookingTool,
    ...(bookingData && {
      bookingData
    }),
    ...(currentStep && {
      currentStep
    }),
    ...(modalDialog && {
      modalDialog
    }),
    ...(nonModalDialog && {
      nonModalDialog
    }),
    ...(reservationOnly && {
      reservationOnly
    }),
    ...(showReservationExpiry && {
      showReservationExpiry
    }),
    ...(userLocation && {
      userLocation
    }),
    ...(iFrameId && {
      iFrameId
    })
  });
  const providedContext = useMemo(() => {
    return {
      appData,
      appDataDispatch
    };
  }, [appData]);

  /**
   * If the current language code is the current i18n language but not a member
   * of the supported language list, the user is viewing the booking tool using
   * a dialect of a supported language. We want to store a reference to this dialect
   * in the appData state so that the user can switch back and forth between other
   * languages and the dialect.
   */
  useEffect(() => {
    if (!isSupportedLanguageCode(i18n.language) && !disableDialectDetection) {
      appDataDispatch({
        type: ActionType.DIALECT_DETECTED
      });
    }
  }, [disableDialectDetection]);
  return <AppContext.Provider value={providedContext} data-sentry-element="unknown" data-sentry-component="AppDataProvider" data-sentry-source-file="AppDataProvider.tsx">
      {children}
    </AppContext.Provider>;
}